import React from 'react';
import { withStyles } from '@material-ui/core';
import TimelineMetric from './TimelineMetric';
import {
  formatDateForDisplay,
  formatHourMinuteToEST,
  getRouteType
} from '../../utils';
import { TIMELINE_METRIC_LABELS } from '../../constants';
import type { ShipmentRouteHistoryItem } from '../../hooks/useShipmentRouteHistory';

const styles = (theme: any) => ({
  timelineItem: {
    display: 'grid',
    gridTemplateColumns: '70px 1fr',
    marginBottom: 0
  },
  timelineTimeRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingLeft: 8
  },
  timelineDate: {
    fontSize: 12,
    color: theme.iconColor,
    fontWeight: 600,
    lineHeight: 1.3
  },
  timelineTimestamp: {
    fontSize: 12,
    color: theme.iconColor,
    fontWeight: 400
  },
  timelineDotRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8
  },
  timelineDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.KPI.KPILabel,
    flexShrink: 0
  },
  timelineLine: {
    width: 2,
    flex: 1,
    minHeight: 80,
    backgroundColor: theme.KPI.KPILabel,
    marginTop: 4,
    marginLeft: 4
  },
  timelineEndDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.KPI.KPILabel,
    flexShrink: 0,
    marginTop: 4
  },
  timelineContent: {
    paddingBottom: 8
  },
  timelineRouteHeader: {
    display: 'flex',
    alignItems: 'center'
  },
  timelineRouteName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.TextColor,
    marginRight: 8
  },
  timelineDetails: {
    fontSize: 12,
    color: theme.gridTextColor,
    marginBottom: 16,
    marginTop: 4
  },
  timelineMetrics: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%'
  }
});

interface TimelineEntryProps {
  classes: Record<string, string>;
  historyItem: ShipmentRouteHistoryItem;
  isLastItem: boolean;
}

const getHistoryRouteType = (historyItem: ShipmentRouteHistoryItem): string => {
  if (historyItem.routeType) return historyItem.routeType;
  if (historyItem.isTrapRoute !== undefined && historyItem.isTrapRoute !== null) {
    return getRouteType(historyItem.isTrapRoute);
  }
  return '-';
};

const TimelineEntry: React.FC<TimelineEntryProps> = ({ classes, historyItem, isLastItem }) => (
  <div className={classes.timelineItem}>
    <div className={classes.timelineTimeRow}>
      <span className={classes.timelineDate}>
        {formatDateForDisplay(historyItem.createdDateTime)}
      </span>
      <span className={classes.timelineTimestamp}>
        {formatHourMinuteToEST(historyItem.createdDateTime)}
      </span>
    </div>
    <div className={classes.timelineRouteHeader}>
      <span className={classes.timelineRouteName}>
        {historyItem.routeName}
      </span>
    </div>
    <div className={classes.timelineDotRow}>
      {!isLastItem && (
        <>
          <div className={classes.timelineDot} />
          <div className={classes.timelineLine} />
          <div className={classes.timelineEndDot} />
        </>
      )}
    </div>
    <div className={classes.timelineContent}>
      <div className={classes.timelineDetails}>
        {historyItem.category} | {historyItem.action} | by {historyItem.createdByEmail || '-'}
      </div>
      <div className={classes.timelineMetrics}>
        <TimelineMetric
          value={historyItem.stopSequence ?? '-'}
          label={TIMELINE_METRIC_LABELS.STOP_SEQUENCE}
        />
        <TimelineMetric
          value={historyItem.totalDeliveryBills || 0}
          label={TIMELINE_METRIC_LABELS.TTL_BILLS}
        />
        <TimelineMetric
          value={historyItem.totalDeliveryStops || 0}
          label={TIMELINE_METRIC_LABELS.STOPS}
        />
        <TimelineMetric
          value={historyItem.totalTime || '-'}
          label={TIMELINE_METRIC_LABELS.TTL_TIME}
        />
        <TimelineMetric
          value={getHistoryRouteType(historyItem)}
          label={TIMELINE_METRIC_LABELS.ROUTE_TYPE}
        />
        <TimelineMetric
          value={historyItem.anchorPointName || '-'}
          label={TIMELINE_METRIC_LABELS.ANCHOR_POINT}
        />
      </div>
    </div>
  </div>
);

export default withStyles(styles)(TimelineEntry);
